import {
  Box,
  Button as ChakraButton,
  Flex,
  IconButton,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  SimpleGrid,
  Tooltip,
  useColorModeValue,
} from "@chakra-ui/react"
import Color from "@tiptap/extension-color"
import Highlight from "@tiptap/extension-highlight"
import Link from "@tiptap/extension-link"
import { TextStyle } from "@tiptap/extension-text-style"
import Underline from "@tiptap/extension-underline"
import { EditorContent, useEditor } from "@tiptap/react"
import StarterKit from "@tiptap/starter-kit"
import { useEffect, useRef } from "react"
import {
  MdFormatBold,
  MdFormatColorFill,
  MdFormatColorText,
  MdFormatItalic,
  MdFormatListBulleted,
  MdFormatListNumbered,
  MdFormatUnderlined,
  MdInsertLink,
} from "react-icons/md"

interface TiptapEditorProps {
  value: string
  onChange: (value: string) => void
  isReadOnly?: boolean
  maxHeight?: string | number
}

const TEMPLATE_LINK_PREFIX = "https://template.local/__tpl__/"

function placeholderToEditorURL(value: string) {
  const match = value.trim().match(/^\{\{([a-zA-Z0-9_]+)\}\}$/)
  if (!match) return value
  return `${TEMPLATE_LINK_PREFIX}${match[1]}`
}

function editorURLToPlaceholder(value: string) {
  if (!value.startsWith(TEMPLATE_LINK_PREFIX)) return value
  const variableName = value.slice(TEMPLATE_LINK_PREFIX.length)
  return variableName ? `{{${variableName}}}` : value
}

function encodeTemplateLinks(html: string) {
  return html.replace(/href="(\{\{[a-zA-Z0-9_]+\}\})"/g, (_, href) => {
    return `href="${placeholderToEditorURL(href)}"`
  })
}

function decodeTemplateLinks(html: string) {
  return html.replace(/href="([^"]+)"/g, (_, href) => {
    return `href="${editorURLToPlaceholder(href)}"`
  })
}

export function TiptapEditor({
  value,
  onChange,
  isReadOnly,
  maxHeight,
}: TiptapEditorProps) {
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.300")
  const toolbarBg = useColorModeValue("gray.50", "whiteAlpha.50")
  const toolbarBorderColor = useColorModeValue("gray.100", "whiteAlpha.200")
  const editorBg = useColorModeValue("white", "bg.input")
  const textColor = useColorModeValue("inherit", "whiteAlpha.900")

  // Використовуємо ref для onChange, щоб уникнути зайвих перестворень та замикань
  const onChangeRef = useRef(onChange)
  useEffect(() => {
    onChangeRef.current = onChange
  }, [onChange])

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TextStyle,
      Color,
      Highlight.configure({ multicolor: true }),
      Link.configure({
        openOnClick: true,
        HTMLAttributes: {
          rel: "noopener noreferrer",
          target: "_blank",
        },
      }),
    ],
    content: encodeTemplateLinks(value),
    editable: !isReadOnly,

    onUpdate: ({ editor }) => {
      const html = decodeTemplateLinks(editor.getHTML())
      onChangeRef.current(html)
    },
  })

  // Синхронізація з зовнішнім значенням ТІЛЬКИ якщо воно ДІЙСНО відрізняється
  useEffect(() => {
    if (!editor || value === undefined) return

    const currentHTML = decodeTemplateLinks(editor.getHTML())

    // Нормалізація для порівняння (ігноруємо порожні параграфи)
    const normalize = (html: string) => {
      if (html === "<p></p>" || html === "") return ""
      return html
    }

    if (normalize(value) !== normalize(currentHTML)) {
      // Якщо редактор не у фокусі, або якщо зовнішнє значення прийшло як "свіже" (наприклад, після завантаження з API)
      // Ми використовуємо isFocused як запобіжник від стрибків курсору під час друку
      if (!editor.isFocused) {
        editor.commands.setContent(encodeTemplateLinks(value), {
          emitUpdate: false,
        })
      }
    }
  }, [value, editor])

  useEffect(() => {
    if (editor && editor.isEditable !== !isReadOnly) {
      editor.setEditable(!isReadOnly)
    }
  }, [isReadOnly, editor])

  if (!editor) {
    return null
  }

  const addLink = () => {
    const url = window.prompt("URL")
    if (url) {
      editor
        .chain()
        .focus()
        .extendMarkRange("link")
        .setLink({ href: placeholderToEditorURL(url) })
        .run()
    }
  }

  const textColors = [
    "#000000",
    "#4A5568",
    "#E53E3E",
    "#DD6B20",
    "#38A169",
    "#3182CE",
    "#805AD5",
    "#D53F8C",
    "#FFFFFF",
    "#A0AEC0",
    "#FEB2B2",
    "#FBD38D",
    "#9AE6B4",
    "#90CDF4",
    "#D6BCFA",
    "#FBB6CE",
  ]

  const highlightColors = [
    "#FFFF00",
    "#9AE6B4",
    "#90CDF4",
    "#FFB6C1",
    "#E6E6FA",
    "#F0E68C",
    "#FFA07A",
    "#D3D3D3",
    "transparent",
  ]

  return (
    <Box
      border={isReadOnly ? "none" : "1px solid"}
      borderColor={borderColor}
      borderRadius="md"
      overflow="hidden"
      bg={isReadOnly ? "transparent" : editorBg}
      minH={isReadOnly ? "auto" : "40px"}
      color={textColor}
    >
      {!isReadOnly && (
        <Flex
          borderBottom="1px solid"
          borderColor={toolbarBorderColor}
          bg={toolbarBg}
          p={1}
          gap={1}
          wrap="wrap"
        >
          <Tooltip label="Pogrubienie">
            <IconButton
              aria-label="Bold"
              icon={<MdFormatBold />}
              size="xs"
              onClick={() => editor.chain().focus().toggleBold().run()}
              isActive={editor.isActive("bold")}
              colorScheme={editor.isActive("bold") ? "blue" : "gray"}
              variant={editor.isActive("bold") ? "solid" : "ghost"}
            />
          </Tooltip>
          <Tooltip label="Kursywa">
            <IconButton
              aria-label="Italic"
              icon={<MdFormatItalic />}
              size="xs"
              onClick={() => editor.chain().focus().toggleItalic().run()}
              isActive={editor.isActive("italic")}
              colorScheme={editor.isActive("italic") ? "blue" : "gray"}
              variant={editor.isActive("italic") ? "solid" : "ghost"}
            />
          </Tooltip>
          <Tooltip label="Podkreślenie">
            <IconButton
              aria-label="Underline"
              icon={<MdFormatUnderlined />}
              size="xs"
              onClick={() => editor.chain().focus().toggleUnderline().run()}
              isActive={editor.isActive("underline")}
              colorScheme={editor.isActive("underline") ? "blue" : "gray"}
              variant={editor.isActive("underline") ? "solid" : "ghost"}
            />
          </Tooltip>
          <Tooltip label="Link">
            <IconButton
              aria-label="Link"
              icon={<MdInsertLink />}
              size="xs"
              onClick={addLink}
              isActive={editor.isActive("link")}
              colorScheme={editor.isActive("link") ? "blue" : "gray"}
              variant={editor.isActive("link") ? "solid" : "ghost"}
            />
          </Tooltip>

          <Popover placement="bottom-start">
            <Tooltip label="Kolor tekstu">
              <Box>
                <PopoverTrigger>
                  <IconButton
                    aria-label="Text Color"
                    icon={<MdFormatColorText />}
                    size="xs"
                    variant="ghost"
                  />
                </PopoverTrigger>
              </Box>
            </Tooltip>
            <PopoverContent w="180px">
              <PopoverBody>
                <SimpleGrid columns={4} spacing={2}>
                  {textColors.map((color) => (
                    <Box
                      key={color}
                      bg={color}
                      w="30px"
                      h="30px"
                      borderRadius="md"
                      cursor="pointer"
                      border="1px solid"
                      borderColor="gray.200"
                      onClick={() =>
                        editor.chain().focus().setColor(color).run()
                      }
                    />
                  ))}
                  <ChakraButton
                    size="xs"
                    gridColumn="span 4"
                    onClick={() => editor.chain().focus().unsetColor().run()}
                  >
                    Reset
                  </ChakraButton>
                </SimpleGrid>
              </PopoverBody>
            </PopoverContent>
          </Popover>

          <Popover placement="bottom-start">
            <Tooltip label="Kolor tła (Zakreślacz)">
              <Box>
                <PopoverTrigger>
                  <IconButton
                    aria-label="Highlight Color"
                    icon={<MdFormatColorFill />}
                    size="xs"
                    variant="ghost"
                  />
                </PopoverTrigger>
              </Box>
            </Tooltip>
            <PopoverContent w="180px">
              <PopoverBody>
                <SimpleGrid columns={4} spacing={2}>
                  {highlightColors.map((color) => (
                    <Box
                      key={color}
                      bg={color === "transparent" ? "white" : color}
                      w="30px"
                      h="30px"
                      borderRadius="md"
                      cursor="pointer"
                      border="1px solid"
                      borderColor="gray.200"
                      position="relative"
                      onClick={() => {
                        if (color === "transparent") {
                          editor.chain().focus().unsetHighlight().run()
                        } else {
                          editor.chain().focus().setHighlight({ color }).run()
                        }
                      }}
                    >
                      {color === "transparent" && (
                        <Box
                          position="absolute"
                          top="50%"
                          left="0"
                          w="100%"
                          h="1px"
                          bg="red.500"
                          transform="rotate(45deg)"
                        />
                      )}
                    </Box>
                  ))}
                </SimpleGrid>
              </PopoverBody>
            </PopoverContent>
          </Popover>

          <Tooltip label="Lista punktowana">
            <IconButton
              aria-label="Bullet List"
              icon={<MdFormatListBulleted />}
              size="xs"
              onClick={() => editor.chain().focus().toggleBulletList().run()}
              isActive={editor.isActive("bulletList")}
              colorScheme={editor.isActive("bulletList") ? "blue" : "gray"}
              variant={editor.isActive("bulletList") ? "solid" : "ghost"}
            />
          </Tooltip>
          <Tooltip label="Lista numerowana">
            <IconButton
              aria-label="Ordered List"
              icon={<MdFormatListNumbered />}
              size="xs"
              onClick={() => editor.chain().focus().toggleOrderedList().run()}
              isActive={editor.isActive("orderedList")}
              colorScheme={editor.isActive("orderedList") ? "blue" : "gray"}
              variant={editor.isActive("orderedList") ? "solid" : "ghost"}
            />
          </Tooltip>
        </Flex>
      )}
      <Box
        p={isReadOnly ? 1 : 2}
        fontSize="sm"
        sx={{
          ".tiptap": {
            outline: "none",
            minH: isReadOnly ? "auto" : "40px",
            maxH: maxHeight || "none",
            overflowY: maxHeight ? "auto" : "visible",
            "ul, ol": {
              paddingLeft: "1.5rem",
            },
            a: {
              color: "blue.400",
              textDecoration: "underline",
            },
            "strong, b": {
              fontWeight: "bold !important",
            },
            "em, i": {
              fontStyle: "italic !important",
            },
            'span[style*="color"]': {
              color: "inherit",
            },
            mark: {
              backgroundColor: "#FFFF00",
              color: "black",
              padding: "0 2px",
              borderRadius: "2px",
            },
          },
        }}
      >
        <EditorContent editor={editor} />
      </Box>
    </Box>
  )
}
